import React, { useState, useEffect } from 'react';
import { bulkCreateShifts, getEmployees, getEmployeeTypeDisplayName, getHospitals } from '../services/api.ts';
import { buildShiftPayloads, DoctorMode, filterEmployeesByTypeAndHospital } from './shiftFormHelpers.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import SpreadsheetImport, { ImportFieldDef, RowOutcome } from './SpreadsheetImport.tsx';
import { getLocalDateString } from '../utils/timezone.ts';
import { resolveEmployeeByName, toUtcFromDateAndTime } from './importUtils.ts';

const EMPLOYEE_TYPES = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'nurse', label: 'CRNA' },
  { value: 'nurse-student-senior', label: 'Senior Student' },
  { value: 'nurse-student-junior', label: 'Junior Student' },
] as const;

interface ShiftImportValue {
  employeeId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  employeeType: string;
  order?: number;
}

const SHIFT_IMPORT_FIELDS: ImportFieldDef[] = [
  { key: 'employee', label: 'Employee / Provider', required: true, aliases: ['employee', 'provider', 'name', 'staff', 'crna', 'anesthesiologist', 'physician', 'md', 'do'], hint: 'Matched by name to your Employee Cards' },
  { key: 'start', label: 'Start Time', required: true, aliases: ['start', 'start time', 'begin', 'in', 'shift start', 'from'], hint: 'e.g. 7:00, 07:00, 7:00 AM, 0700' },
  { key: 'end', label: 'End Time', required: true, aliases: ['end', 'end time', 'finish', 'out', 'shift end', 'to'] },
  { key: 'order', label: 'Pecking Order', required: false, aliases: ['order', 'pecking', 'pecking order', 'rank', 'priority'], hint: 'Optional — only used for doctors' },
];

interface Employee {
  employeeId: string;
  firstName: string;
  lastName: string;
  employeeType: string;
  homeHospitalId?: string;
}

interface Hospital {
  hospitalId: string;
  name: string;
}

const ShiftForm: React.FC = () => {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [employeeType, setEmployeeType] = useState('');
  const [doctorMode, setDoctorMode] = useState<DoctorMode>('');
  const [homeHospitalFilter, setHomeHospitalFilter] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [shiftDate, setShiftDate] = useState('');
  const [orderMap, setOrderMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importDate, setImportDate] = useState<string>(getLocalDateString());

  useEffect(() => {
    loadEmployees();
    loadHospitals();
  }, []);

  // Turn one mapped spreadsheet row into a shift to create (or an error explaining why not).
  // The provider's role comes from their Employee Card, so the file doesn't need a "type" column.
  const transformShiftRow = (mapped: Record<string, string>): RowOutcome<ShiftImportValue> => {
    const empRes = resolveEmployeeByName(employees, mapped.employee);
    if (!empRes.ok) return { ok: false, error: empRes.error };
    const startTimeUtc = toUtcFromDateAndTime(importDate, mapped.start);
    if (!startTimeUtc) return { ok: false, error: `couldn't read start time "${mapped.start}"` };
    const endTimeUtc = toUtcFromDateAndTime(importDate, mapped.end);
    if (!endTimeUtc) return { ok: false, error: `couldn't read end time "${mapped.end}"` };
    if (new Date(endTimeUtc).getTime() <= new Date(startTimeUtc).getTime()) {
      return { ok: false, error: 'end time is not after start time' };
    }
    const value: ShiftImportValue = {
      employeeId: empRes.employee.employeeId,
      startTimeUtc,
      endTimeUtc,
      employeeType: empRes.employee.employeeType,
    };
    if (empRes.employee.employeeType === 'doctor' && mapped.order && mapped.order.trim()) {
      const n = parseInt(mapped.order, 10);
      if (!isNaN(n) && n >= 1) value.order = n;
    }
    return { ok: true, value };
  };

  const commitImportedShifts = async (values: ShiftImportValue[]): Promise<string> => {
    await bulkCreateShifts(values);
    return `Imported ${values.length} shift${values.length === 1 ? '' : 's'} for ${importDate}.`;
  };

  const loadEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadHospitals = async () => {
    try {
      const response = await getHospitals();
      setHospitals(response.hospitals || []);
    } catch (error) {
      console.error('Error loading hospitals:', error);
    }
  };

  const filteredEmployees = filterEmployeesByTypeAndHospital(employees, employeeType, homeHospitalFilter);

  const handleTypeChange = (type: string) => {
    setEmployeeType(type);
    setDoctorMode('');
    setHomeHospitalFilter('');
    setSelectedEmployeeIds([]);
    setOrderMap({});
    setShiftDate('');
    setStartTime('');
    setEndTime('');
    setMessage(null);
  };

  const handleDoctorModeChange = (mode: DoctorMode) => {
    setDoctorMode(mode);
    setSelectedEmployeeIds([]);
    setOrderMap({});
    setShiftDate('');
    setStartTime('');
    setEndTime('');
    setMessage(null);
  };

  const handleEmployeeToggle = (empId: string) => {
    setSelectedEmployeeIds(prev => {
      if (prev.includes(empId)) {
        const next = prev.filter(id => id !== empId);
        if (doctorMode === 'pecking-order') {
          setOrderMap(om => {
            const copy = { ...om };
            delete copy[empId];
            return copy;
          });
        }
        return next;
      } else {
        if (doctorMode === 'pecking-order') {
          const nextOrder = Object.keys(orderMap).length + 1;
          setOrderMap(om => ({ ...om, [empId]: nextOrder }));
        }
        return [...prev, empId];
      }
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredEmployees.map(e => e.employeeId);
    setSelectedEmployeeIds(allIds);
    if (doctorMode === 'pecking-order') {
      const newOrderMap: Record<string, number> = {};
      allIds.forEach((id, i) => { newOrderMap[id] = i + 1; });
      setOrderMap(newOrderMap);
    }
  };

  const handleDeselectAll = () => {
    setSelectedEmployeeIds([]);
    setOrderMap({});
  };

  const handleOrderChange = (empId: string, value: string) => {
    const num = parseInt(value);
    if (value === '') {
      setOrderMap(om => {
        const copy = { ...om };
        delete copy[empId];
        return copy;
      });
    } else if (!isNaN(num) && num >= 1) {
      setOrderMap(om => ({ ...om, [empId]: num }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const payload = buildShiftPayloads({
      employeeType,
      doctorMode,
      shiftDate,
      startTime,
      endTime,
      selectedEmployeeIds,
      orderMap,
    });

    if (!payload.ok) {
      setMessage({ type: 'error', text: payload.error });
      return;
    }

    setLoading(true);
    try {
      const result = await bulkCreateShifts(payload.shifts);
      setMessage({ type: 'success', text: result.message || `${payload.shifts.length} shift${payload.shifts.length === 1 ? '' : 's'} created successfully` });
      setSelectedEmployeeIds([]);
      setOrderMap({});
      setStartTime('');
      setEndTime('');
      setShiftDate('');
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create shifts'
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = selectedEmployeeIds.length;
  const isDoctor = employeeType === 'doctor';
  const isPeckingOrder = isDoctor && doctorMode === 'pecking-order';
  const showRestOfForm = Boolean(employeeType) && (!isDoctor || doctorMode !== '');

  // For doctor pecking order display, sort selected doctors by their order value
  const selectedDoctors = isPeckingOrder
    ? selectedEmployeeIds
        .map(id => ({ id, employee: employees.find(e => e.employeeId === id)!, order: orderMap[id] ?? 0 }))
        .filter(d => d.employee)
        .sort((a, b) => (a.order || 999) - (b.order || 999))
    : [];

  return (
    <div>
      <ReadOnlyBanner message="Shift creation is restricted to administrators. You have read-only access." />

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      {isAdmin && (
        <SpreadsheetImport<ShiftImportValue>
          title="Import shifts from a spreadsheet"
          description="Upload an Excel (.xlsx) or CSV file of who's working — for example a QGenda or Excel export. Match your columns below, review the preview, then import. Each provider's role (CRNA, anesthesiologist, student) comes from their Employee Card, so the file only needs names and times."
          fields={SHIFT_IMPORT_FIELDS}
          transformRow={transformShiftRow}
          onCommit={commitImportedShifts}
          ready={employees.length > 0}
          notReadyMessage="Loading employees…"
          extraControls={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label htmlFor="shiftImportDate" style={{ margin: 0, whiteSpace: 'nowrap' }}>Date for these shifts:</label>
              <input
                id="shiftImportDate"
                type="date"
                className="form-control"
                value={importDate}
                onChange={(e) => setImportDate(e.target.value)}
                style={{ maxWidth: '190px' }}
              />
              <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                Start/end times in the file are combined with this date (local time).
              </span>
            </div>
          }
        />
      )}

      {isAdmin && (
      <form onSubmit={handleSubmit}>
        {/* Step 1: Employee Type */}
        <div className="form-group">
          <label>Employee Type</label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {EMPLOYEE_TYPES.map(t => (
              <label key={t.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="employeeType"
                  value={t.value}
                  checked={employeeType === t.value}
                  onChange={() => handleTypeChange(t.value)}
                />
                {t.label}
              </label>
            ))}
          </div>
        </div>

        {/* Step 2a: Doctor Mode (pecking order vs shift doctor) */}
        {isDoctor && (
          <div className="form-group">
            <label>Doctor Type</label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="doctorMode"
                  value="pecking-order"
                  checked={doctorMode === 'pecking-order'}
                  onChange={() => handleDoctorModeChange('pecking-order')}
                />
                Pecking Order
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="doctorMode"
                  value="shift"
                  checked={doctorMode === 'shift'}
                  onChange={() => handleDoctorModeChange('shift')}
                />
                Shift Doctor
              </label>
            </div>
          </div>
        )}

        {/* Step 2b: Time Window (or Date for pecking order) */}
        {showRestOfForm && (
          <>
            {isPeckingOrder ? (
              <div className="form-group">
                <label htmlFor="shiftDate">Shift Date (Local)</label>
                <input
                  type="date"
                  id="shiftDate"
                  value={shiftDate}
                  onChange={e => setShiftDate(e.target.value)}
                  required
                />
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="startTime">Start Time (Local)</label>
                  <input
                    type="datetime-local"
                    id="startTime"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="endTime">End Time (Local)</label>
                  <input
                    type="datetime-local"
                    id="endTime"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 3a: Optional Home Hospital Filter */}
            <div className="form-group">
              <label htmlFor="homeHospitalFilter">Filter by Home Hospital (Optional)</label>
              <select
                id="homeHospitalFilter"
                value={homeHospitalFilter}
                onChange={e => setHomeHospitalFilter(e.target.value)}
              >
                <option value="">All hospitals</option>
                {hospitals.map(h => (
                  <option key={h.hospitalId} value={h.hospitalId}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 3: Employee Multi-Select */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ margin: 0 }}>
                  Select {getEmployeeTypeDisplayName(employeeType)}s ({selectedCount} of {filteredEmployees.length} selected)
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={handleSelectAll}>
                    Select All
                  </button>
                  <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={handleDeselectAll}>
                    Deselect All
                  </button>
                </div>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '4px' }}>
                {filteredEmployees.length === 0 ? (
                  <div style={{ padding: '8px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No {getEmployeeTypeDisplayName(employeeType)}s found</div>
                ) : (
                  filteredEmployees.map(emp => (
                    <label key={emp.employeeId} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedEmployeeIds.includes(emp.employeeId)}
                        onChange={() => handleEmployeeToggle(emp.employeeId)}
                      />
                      <span className="checkbox-text">{emp.lastName}, {emp.firstName}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Step 4: Doctor Pecking Order */}
            {isPeckingOrder && selectedDoctors.length > 0 && (
              <div className="form-group">
                <label>Pecking Order</label>
                <small className="form-text" style={{ display: 'block', marginBottom: '8px' }}>
                  Lower numbers have higher priority. Adjust the order values as needed.
                </small>
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                  {selectedDoctors.map(({ id, employee, order }) => (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                      <input
                        type="number"
                        min="1"
                        value={order || ''}
                        onChange={e => handleOrderChange(id, e.target.value)}
                        style={{ width: '60px' }}
                        placeholder="#"
                      />
                      <span>{employee.lastName}, {employee.firstName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <button type="submit" className="btn btn-primary" disabled={loading || selectedCount === 0}>
          {loading
            ? 'Creating...'
            : selectedCount <= 1
              ? 'Create Shift'
              : `Create ${selectedCount} Shifts`}
        </button>
      </form>
      )}
    </div>
  );
};

export default ShiftForm;
